"use client";

// ============================================================
// CLUBHUB – Ein Aushang
// ============================================================
// Ein Zettel am Brett: fuer wen, Ueberschrift, Text in Absaetzen,
// wer und wann, wie lange er haengt. Lange Texte klappen nach zwei
// Absaetzen zu — das Brett soll man ueberfliegen koennen.
// Angeheftete liegen hoeher (Erhebung, Nadel in Hausrot); was
// abgelaufen ist, tritt zurueck.
// ============================================================
import { useState } from "react";
import { Clock, Ellipsis, Pencil, Pin, PinOff, Trash2 } from "lucide-react";
import { Avatar, DropdownItem, DropdownMenu, DropdownSeparator, confirmDialog, toast } from "@/components/ui";
import { istFehler } from "@/lib/action-result";
import type { AushangView } from "@/lib/pinnwand/views";
import { absaetze, istAbgelaufen, laeuftBaldAb } from "@/lib/pinnwand/logik";
import { datumKurz, tageRelativ, tageZwischen, uhrzeit } from "@/lib/gruppen/datum";
import { cn, formatDate } from "@/lib/utils";
import { usePinnwand } from "./pinnwand-bestand";
import { ZielEtikett, namensTeile } from "./pinnwand-teile";

const KURZ_ABSAETZE = 2;
const KURZ_ZEICHEN = 360;

export function AushangKarte({ aushang: a, onBearbeiten }: { aushang: AushangView; onBearbeiten: () => void }) {
  const { heute, jetzt, rechte, anheften, aushangLoeschen } = usePinnwand();
  const [offen, setOffen] = useState(false);
  const teile = absaetze(a.body);
  const lang = teile.length > KURZ_ABSAETZE || a.body.length > KURZ_ZEICHEN;
  const gezeigt = lang && !offen ? teile.slice(0, KURZ_ABSAETZE) : teile;
  const abgelaufen = istAbgelaufen(a, jetzt);
  const bald = laeuftBaldAb(a, jetzt);
  const wann = tageRelativ(a.createdAt, heute);

  async function loeschen() {
    const ja = await confirmDialog({
      title: `„${a.title}“ löschen?`,
      description: "Der Aushang verschwindet für alle. Soll er nur nicht mehr oben stehen, lass ihn ablaufen.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ja) return;
    const r = await aushangLoeschen(a.id);
    if (istFehler(r)) toast.error(r.error);
    else toast.success("Aushang gelöscht");
  }

  let ablauf: React.ReactNode = null;
  if (a.expiresAt) {
    const tage = tageZwischen(heute, a.expiresAt);
    ablauf = abgelaufen
      ? `abgelaufen ${datumKurz(a.expiresAt)}`
      : tage === 0
        ? "läuft heute ab"
        : bald
          ? `läuft in ${tage} ${tage === 1 ? "Tag" : "Tagen"} ab`
          : `bis ${formatDate(a.expiresAt)}`;
  }

  return (
    <article
      className={cn(
        "bg-bg-elevated mb-4 break-inside-avoid rounded-xl p-4 ring-1 ring-black/[0.04] ring-inset dark:ring-white/[0.06]",
        a.isPinned && !abgelaufen ? "shadow-e2" : "shadow-e1",
        abgelaufen && "opacity-70",
      )}
    >
      <header className="flex items-center gap-2">
        <ZielEtikett audience={a.audience} groupId={a.groupId} />
        {a.isPinned && !abgelaufen && (
          <span className="text-accent inline-flex items-center" title="Oben angeheftet">
            <Pin className="h-3.5 w-3.5 fill-current" aria-hidden />
            <span className="sr-only">Angeheftet</span>
          </span>
        )}
        <span className="flex-1" />
        {rechte.schreiben && (
          <DropdownMenu
            trigger={
              <button
                type="button"
                aria-label={`Aktionen für „${a.title}“`}
                className="text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary duration-fast -my-1 -mr-1.5 grid h-8 w-8 place-items-center rounded-lg transition-colors"
              >
                <Ellipsis className="h-4 w-4" aria-hidden />
              </button>
            }
          >
            {!abgelaufen && (
              <DropdownItem
                onClick={async () => {
                  const r = await anheften(a.id, !a.isPinned);
                  if (istFehler(r)) toast.error(r.error);
                  else toast.success(a.isPinned ? "Nicht mehr angeheftet" : "Oben angeheftet");
                }}
              >
                {a.isPinned ? <PinOff className="h-4 w-4" aria-hidden /> : <Pin className="h-4 w-4" aria-hidden />}
                {a.isPinned ? "Lösen" : "Oben anheften"}
              </DropdownItem>
            )}
            <DropdownItem onClick={onBearbeiten}>
              <Pencil className="h-4 w-4" aria-hidden />
              {abgelaufen ? "Bearbeiten und verlängern" : "Bearbeiten"}
            </DropdownItem>
            <DropdownSeparator />
            <DropdownItem variant="danger" onClick={() => void loeschen()}>
              <Trash2 className="h-4 w-4" aria-hidden />
              Löschen
            </DropdownItem>
          </DropdownMenu>
        )}
      </header>

      <h3 className="text-heading-sm text-text-primary mt-2.5 break-words hyphens-auto">{a.title}</h3>

      <div className="text-body-sm text-text-secondary mt-1.5 space-y-2 break-words">
        {gezeigt.map((p, i) => (
          <p key={i} className="whitespace-pre-line">
            {lang && !offen && i === gezeigt.length - 1 && p.length > KURZ_ZEICHEN ? `${p.slice(0, KURZ_ZEICHEN).trimEnd()} …` : p}
          </p>
        ))}
      </div>
      {lang && (
        <button
          type="button"
          onClick={() => setOffen((o) => !o)}
          aria-expanded={offen}
          className="text-caption text-accent hover:text-accent-hover mt-1.5 font-medium"
        >
          {offen ? "Weniger" : "Weiterlesen"}
        </button>
      )}

      <footer className="border-border-subtle text-caption text-text-tertiary mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t pt-2.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <Avatar {...namensTeile(a.authorName)} size="sm" className="h-5 w-5 text-[9px]" hint={false} />
          <span className="text-text-secondary truncate">{a.authorName}</span>
        </span>
        <span aria-hidden>·</span>
        <time dateTime={a.createdAt} className="numeric">
          {wann === "heute" ? `heute, ${uhrzeit(a.createdAt)}` : wann}
        </time>
        {ablauf && (
          <span className={cn("numeric ml-auto inline-flex items-center gap-1", bald && !abgelaufen && "text-warning font-medium")}>
            <Clock className="h-3 w-3" aria-hidden />
            {ablauf}
          </span>
        )}
      </footer>
    </article>
  );
}
